/**
 * Section → Layout Adapter (Visualizer v2, §C / Phase C1)
 * ======================================================
 *
 * Converts the canonical positioned-section model (`RoomSurfaces`) into the
 * viewer's existing `RoomBoardLayouts` (BoardRect lists), so `Room3DViewer` can
 * render the editor's section state without touching its rendering internals.
 *
 * Each surface is resolved (W&CB fills carved by SlatWall inserts). A fill with
 * nothing over it is tiled by the existing `boardLayoutEngine` generators. A
 * fill with an inset over it is drawn BY COURSE — the same runs the panel count
 * uses (calculations/sections/courses.ts): rows carry on at the same heights
 * either side of the inset, and boards are cut or notched where it sits.
 *
 * C1 renders the default (least-waste) layout per region; honoring a section's
 * optimizer strategy/length-lock in the 3D view is a C3 enhancement.
 *
 * Branch-exclusive to `3d-visualizer-v3`.
 */

import { RoomSurfaces, Surface } from '../../../types/sections';
import { resolveSurface, ResolvedFill, rectOf } from '../../../calculations/sections/resolver';
import { layoutCourses, boardPieces, wholeRowPieces, partRowPieces } from '../../../calculations/sections/courses';
import { Outline, surfaceOutline } from '../../../calculations/sections/outline';
import { surfaceTrims, TRIM_H } from '../../../calculations/sections/trims';
import { CeilingPlane } from '../../../calculations/ceiling/profile';
import { getProductColorHex } from './materialFactory';
import { SLATWALL_COVERAGE_HEIGHT } from '../../../calculations/constants';
import {
  generateHorizontalWCBSection,
  generateVerticalWCBSection,
  generateSlatWallSection,
} from './boardLayoutEngine';
import {
  BoardRect,
  OpeningShape,
  TrimPiece,
  WallBoardLayout,
  WallSectionLayout,
  CeilingBoardLayout,
  CeilingPlaneLayout,
  CeilingPlaneFace,
  RoomBoardLayouts,
} from './types';

/** Width of each trim's visible face, in feet. Approximate until the real profiles arrive. */
const TRIM_FACE_FT: Record<string, number> = { JSnap: 1 / 12, H: 1.5 / 12 };
const DEFAULT_TRIM_FACE_FT = 1.5 / 12;

/**
 * The trim on a surface as drawable pieces. An H-Trim straddles the line it
 * joins; every other trim sits on its own section, running inward from the
 * edge it finishes.
 */
export function surfaceTrimPieces(surface: Surface): TrimPiece[] {
  const pieces: TrimPiece[] = [];
  for (const run of surfaceTrims(surface)) {
    if (!run.trim) continue;
    const face = TRIM_FACE_FT[run.trim] ?? DEFAULT_TRIM_FACE_FT;
    if (run.rake) {
      // Along the sloped top of the wall: the face hangs below the rake line.
      pieces.push({
        x: run.from, y: 0, width: run.to - run.from, height: face, rake: run.rake,
        color: getProductColorHex(run.color), kind: run.trim, productType: run.finishes,
      });
      continue;
    }
    const horizontal = run.side === 'top' || run.side === 'bottom';
    // Across the edge: centred on the line for H; otherwise on the PRODUCT side
    // of it — inward for a section's own edge, outward for an opening's (the
    // trim caps the board around the hole, not the hole).
    const intoSection = run.side === 'top' || run.side === 'right' ? -1 : 1;
    const inward = run.owner === 'opening' ? -intoSection : intoSection;
    const start = run.trim === TRIM_H ? run.at - face / 2 : inward === 1 ? run.at : run.at - face;
    pieces.push({
      x: horizontal ? run.from : start,
      y: horizontal ? start : run.from,
      width: horizontal ? run.length : face,
      height: horizontal ? face : run.length,
      color: getProductColorHex(run.color),
      kind: run.trim,
      productType: run.finishes,
    });
  }
  return pieces;
}

/** Shift every board's x by dx (regions don't start at x=0 once carved). */
function offsetX(boards: BoardRect[], dx: number): BoardRect[] {
  return dx === 0 ? boards : boards.map((b) => ({ ...b, x: b.x + dx }));
}

/**
 * Boards for one W&CB fill. Every board says which way it runs, so the mesh
 * builder never has to guess from the shape of a short or notched piece.
 */
function fillBoards(fill: ResolvedFill, outline: Outline | null): BoardRect[] {
  const { section } = fill;
  const vertical = section.orientation === 'Vertical';
  const gen = vertical ? generateVerticalWCBSection : generateHorizontalWCBSection;

  // Nothing over it on a plain rectangle — tile each region as before.
  if (fill.holes.length === 0 && !outline) {
    return fill.regions.flatMap((region) =>
      offsetX(gen(region.width, region.height, region.y, section.color), region.x).map((b) => ({ ...b, vertical })));
  }

  // Something over it, or a wall with a shaped top — one course run at a time,
  // each board cut to the shape the overlays leave (see courses.ts). Under a
  // slope the runs are already cut to where the wall is; the rake itself is
  // applied when the boards are drawn (clipping planes from the outline).
  const boards: BoardRect[] = [];
  let boardId = 0;
  for (const run of layoutCourses(rectOf(section), section.orientation ?? 'Horizontal', fill.holes, undefined, outline)) {
    const whole = vertical
      ? offsetX(generateVerticalWCBSection(run.depth, run.length, run.rect.y, section.color), run.rect.x)
      : offsetX(generateHorizontalWCBSection(run.length, run.depth, run.rect.y, section.color), run.rect.x);
    for (const board of whole) {
      const pieces = boardPieces(board, fill.holes, section.orientation ?? 'Horizontal');
      const notched = pieces.length !== 1 || pieces[0] !== board; // boardPieces returns the board itself when untouched
      // One board, however many rectangles it takes to draw its shape.
      boardId += 1;
      for (const piece of pieces) {
        // A notched board's pieces remember where their course starts, so a
        // piece cut narrower than the course is drawn as the course's profile
        // clipped — in step with the board's other pieces.
        boards.push({ ...board, ...piece, vertical, isCut: board.isCut || notched, boardId, ...(notched ? { courseStart: vertical ? board.x : board.y, courseSize: vertical ? board.width : board.height } : {}) });
      }
    }
  }
  return boards;
}

/** Convert one wall/ceiling Surface into a WallBoardLayout (resolved + carved). */
export function surfaceToWallBoardLayout(surface: Surface, wallId: string): WallBoardLayout {
  const resolved = resolveSurface(surface);
  const sections: WallSectionLayout[] = [];
  const outline = surfaceOutline(surface);

  // W&CB fills — one layout per fill, so the mesh builder sees all of a
  // section's boards together (it needs the neighbours to know where a board's
  // tongue is covered by the next course and where it must be cut back).
  for (const fill of resolved.fills) {
    if (fill.section.productType !== 'WCB') continue;
    const boards = fillBoards(fill, outline);
    if (boards.length === 0) continue;
    sections.push({ sectionType: 'FullWall', yStart: fill.section.y, height: fill.section.height, boards, sectionId: fill.section.id, productType: 'WCB' });
  }

  // SlatWall inserts — one solid rect each.
  for (const insert of resolved.inserts) {
    if (insert.productType !== 'SlatWall') continue;
    // One rectangle of rows — or, where a window or door cuts into it, the
    // whole-row rectangles left around the opening.
    const boards = wholeRowPieces(rectOf(insert), resolved.openings, SLATWALL_COVERAGE_HEIGHT).flatMap((piece) =>
      offsetX(generateSlatWallSection(piece.width, Math.round(piece.height / SLATWALL_COVERAGE_HEIGHT), piece.y, insert.color), piece.x));
    // …and the strips of rows an opening cuts into (over a door, under a sill).
    for (const strip of partRowPieces(rectOf(insert), resolved.openings, SLATWALL_COVERAGE_HEIGHT)) {
      boards.push({
        x: strip.x, y: strip.y, width: strip.width, height: strip.height,
        color: getProductColorHex(insert.color), productType: 'SlatWall', isCut: true, rowY: strip.rowY,
      });
    }
    if (boards.length === 0) continue;
    sections.push({ sectionType: 'SlatWall', yStart: insert.y, height: insert.height, boards, sectionId: insert.id, productType: 'SlatWall' });
  }

  return {
    wallId,
    wallLength: surface.width,
    wallHeight: surface.height,
    sections,
    outline: outline ?? undefined,
    trims: surfaceTrimPieces(surface),
    openings: (surface.openings ?? []).map((o) => {
      const shape: OpeningShape = {
        x: o.x, y: o.y, width: o.width, height: o.height, kind: o.kind, style: o.style,
        key: o.link ? `link:${o.link}` : `${wallId}:${o.id}`,
      };
      if (o.kind === 'door') { shape.hinge = o.hinge ?? 'left'; shape.swing = o.swing ?? 'in'; }
      if (o.kind === 'passage' && o.through) {
        // Finished through the wall in the panel it is cut into.
        const at = { x: o.x + o.width / 2, y: o.y + o.height / 2 };
        const product = surface.sections.find((s) => at.x >= s.x && at.x <= s.x + s.width && at.y >= s.y && at.y <= s.y + s.height) ?? surface.sections[0];
        shape.through = o.through;
        shape.color = getProductColorHex(product?.color ?? 'White');
      }
      return shape;
    }),
  };
}

/**
 * Convert a ceiling Surface into a CeilingBoardLayout. Boards follow each
 * section's orientation exactly as the panel calc counts them: 'Horizontal'
 * runs them along the ceiling's length (surface x), 'Vertical' along its width.
 */
export function surfaceToCeilingLayout(surface: Surface): CeilingBoardLayout {
  const resolved = resolveSurface(surface);
  const boards: BoardRect[] = [];
  let sectionId: string | undefined;
  for (const fill of resolved.fills) {
    if (fill.section.productType !== 'WCB') continue;
    if (sectionId === undefined) sectionId = fill.section.id; // mesh tag; with several sections the overlay does the picking
    boards.push(...fillBoards(fill, null));
  }
  return { length: surface.width, width: surface.height, boards, sectionId, trims: surfaceTrimPieces(surface) };
}

/**
 * Convert the editor's RoomSurfaces into the viewer's RoomBoardLayouts.
 * Walls map by index to the viewer's box order [front, right, back, left]
 * (matching `roomSurfacesFromWallConfigs` / `createFullRoomWallConfigs`).
 * A profiled ceiling (`room.ceilingPlanes`) needs its planes' geometry
 * (`ceilingGeometry(room).planes`) to be placed; without it, it is not drawn.
 */
export function roomSurfacesToLayouts(room: RoomSurfaces, planes?: CeilingPlane[]): RoomBoardLayouts {
  const wallIds = ['front', 'right', 'back', 'left'];
  const out: RoomBoardLayouts = {
    walls: room.walls.map((s, i) => surfaceToWallBoardLayout(s, wallIds[i] ?? s.id)),
    ceiling: room.ceiling ? surfaceToCeilingLayout(room.ceiling) : undefined,
  };
  if (room.ceilingPlanes && planes) {
    const list: CeilingPlaneLayout[] = [];
    for (const plane of planes) {
      const surface = room.ceilingPlanes[plane.id];
      if (!surface) continue;
      list.push({ id: plane.id as CeilingPlaneFace, layout: surfaceToWallBoardLayout(surface, plane.id), plane });
    }
    if (list.length) out.ceilingPlanes = list;
  }
  return out;
}
